import { ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button, Card, EmptyState } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { rolesForPage, type PageId } from '@/features/navigation/pages';
import { PageContainer } from '@/layouts/PageContainer';

/**
 * No access: the role lacks the page's capability, or (`reason="branch"`,
 * RL-1176) the role no longer holds any branch after `me` was reloaded.
 * RL-1821: whoever approves permission changes (the Super admin) is not told
 * to "Ask the Super admin"; they see whose page it is (e.g. the clinic manager's).
 */
export function ForbiddenPage({
  reason = 'role',
  pageId,
}: {
  reason?: 'role' | 'branch';
  pageId?: PageId;
}) {
  const { t } = useTranslation();
  const { signOut, hasPermission } = useSession();
  const branch = reason === 'branch';
  const owners = pageId ? rolesForPage(pageId) : [];
  const body = branch
    ? t('errors.noBranchBody')
    : !hasPermission('permissions:approve')
      ? t('errors.forbiddenBody')
      : owners.length
        ? t('errors.forbiddenPageFor', {
            roles: new Intl.ListFormat('en', { type: 'conjunction' }).format(
              owners.map((r) => t(`errors.pageOwners.${r}`)),
            ),
          })
        : t('errors.forbiddenNotInRole');
  return (
    <PageContainer>
      <Card>
        <EmptyState
          icon={<ShieldAlert size={22} />}
          title={t(branch ? 'errors.noBranchTitle' : 'errors.forbiddenTitle')}
          body={body}
          action={
            branch ? (
              <Button size="sm" onClick={() => void signOut()}>
                {t('auth.signOut')}
              </Button>
            ) : (
              <Link to="/dashboard">
                <Button size="sm">Dashboard</Button>
              </Link>
            )
          }
        />
      </Card>
    </PageContainer>
  );
}
